import { useDeleteStoryboards } from "@/hooks/storyboards/useDeleteStoryboards";
import { useProjectLaunch } from "@/hooks/useProjectLaunch";
import { fetchFileTree, resetCodebaseState } from "@/store/codebaseSlice";
import { fetchGitStatus, resetGithubState } from "@/store/gitStatusSlice";
import {
  selectHoveredTempoElement,
  selectMultiSelectedTempoElements,
  selectSelectedTempoElement,
  setExtractedComponentFile,
  setExtractedComponentFilePath,
  setSelectedElementKey,
} from "@/store/iframeSlice";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import useDesignSystemStore from "@/store/zustand/aiDesignSystemBuilder";
import {
  useCanvasStore,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/store/zustand/canvasStore";
import {
  RemoveJsxChange,
  UnknownChange,
} from "@/store/zustand/changeLedgerTypes";
import { EditorContext, useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore, { LayoutMode } from "@/store/zustand/layoutStore";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import {
  useFeaturePrdForCanvas,
  useSupabaseStore,
} from "@/store/zustand/supabaseStore";
import { generateJsonStream } from "@/utils/ai/utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { track } from "@/utils/events/tracker";
import {
  getComponentFileFromExtractedElements,
  getExtractedElement,
} from "@/utils/extractElementsUtils";
import { getTreeElementLookupToSend } from "@/utils/iframeUtils";
import { TempoElement } from "@/utils/tempoElement";
import _ from "lodash";
import Mousetrap from "mousetrap";
import "mousetrap/plugins/global-bind/mousetrap-global-bind";
import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { Canvas, NavTreeNode } from "@/utils/types";
import {
  fixJson,
  logAxiosError,
  useActiveCanvas,
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
  usePrevious,
  getAxiosErorrMessage,
  getUnexpiredToken,
} from "@/utils/utils";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import useSupabaseConnectStore from "@/store/zustand/supabaseConnectStore";
import { useSearchParams } from "react-router";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import axios from "axios";
import { constructFigmaMessage } from "@/utils/figmaUtils";
import { getMachineForCanvas } from "@/utils/checkRedisHealth";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import { useSession } from "@clerk/clerk-react";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { SignedInSessionResource } from "@clerk/types";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import useActivityStore from "@/store/zustand/activityStore";
import { useIframeStore } from "@/store/zustand/iframeStore";
import { useProjectCacheStore } from "@/store/zustand/projectHistoryStore";

const getGetAndFileTreeFunction = () => {
  return _.throttle(
    (session: SignedInSessionResource, canvasId: any, dispatch: any) => {
      if (!session) {
        return;
      }

      dispatch(
        fetchGitStatus({
          session,
          canvasId,
        })
      );
      dispatch(
        fetchFileTree({
          session,
          canvasId,
        })
      );
    },
    1000,
    { leading: true }
  );
};

/**
 * These are all side effects/background effects that do not directly affect any renders
 * but apply in all cases when the editor is open
 */
export const EditorSideEffects = React.memo(() => {
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage
  );

  const { session } = useSession();

  const { supabaseClient } = useSupabase();

  const canvasId = useCanvasId();
  const dispatch = useDispatch<any>();

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );
  const scopeLookup = useCodebaseStore((state) => state.scopeLookup);
  const prevChanges = useCodebaseStore((state) => state.prevChanges);
  const projectLaunchStatus = useCodebaseStore(
    (state) => state.projectLaunchStatus
  );
  const openFile = useCodebaseStore((state) => state.openFile);
  const clearPrevChanges = useCodebaseStore((state) => state.clearPrevChanges);
  const closeAllFiles = useCodebaseStore((state) => state.closeAllFiles);

  const iframeChangedKey = useSelector(
    (state: any) => state.iframe.iframeChangedKey
  );

  // Track the element that need to be highlighted/selected
  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);
  const hoveredElement: TempoElement = useSelector(selectHoveredTempoElement);
  const multiSelectedElements: TempoElement[] = useSelector(
    selectMultiSelectedTempoElements
  );

  const pendingSelectionCodebaseId = useIframeStore(
    (state) => state.pendingSelectionCodebaseId
  );
  const setPendingSelectionCodebaseId = useIframeStore(
    (state) => state.setPendingSelectionCodebaseId
  );

  const multiSelectedElementKeys: string[] = useSelector(
    (state: any) => state.iframe.multiSelectedElementKeys
  );

  const prevSelectedElement = usePrevious(selectedElement);
  const prevMultiSelectedElementKeys = usePrevious(multiSelectedElementKeys);
  const prevHoveredElement = usePrevious(hoveredElement);

  // Plan info
  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  // For the left nav
  const nodeOpenDict = useCanvasStore((store) => store.nodeOpenDict);
  const setNodeOpenDict = useCanvasStore((store) => store.setNodeOpenDict);

  const elementKeyToNavNodeLookup = useCanvasStore(
    (store) => store.elementKeyToNavNodeLookup
  );
  const codebaseIdToNavNodeLookup = useCanvasStore(
    (store) => store.codebaseIdToNavNodeLookup
  );

  const project = useActiveProject();

  const selectedNavTreeNode = useSelectedNavTreeNode();
  const multiselectedNavTreeNodes = useMultiselectedNavTreeNode();

  const selectedTreeElement = (treeElementLookup || {})[
    selectedElement.codebaseId
  ];

  // Project from DB
  const canvas = useActiveCanvas();
  const mainCanvas = useActiveMainCanvas();

  const filesToReload = useCodebaseStore((state) => state.filesToReload);
  const editorContext = useCodebaseStore((state) => state.editorContext);
  const editorState = useCodebaseStore((state) => state.editor);
  const loadAllNewFiles = useCodebaseStore((state) => state.loadAllNewFiles);

  const triggerDeleteElement = useCanvasStore(
    (state) => state.triggerDeleteElement
  );
  const setTriggerDeleteElement = useCanvasStore(
    (state) => state.setTriggerDeleteElement
  );

  // Other hooks
  useProjectLaunch();

  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const { deleteStoryboards, renderConfirmDeleteDialog } =
    useDeleteStoryboards();

  const onDbSnapshot = useSupabaseStore((state) => state.onDbSnapshot);
  const [searchParams, setSearchParams] = useSearchParams();

  const figmaContextId = searchParams.get("figmaContextId");
  const generatePRDFlow: boolean = searchParams.get("generate-prd") === "t";

  const prevGeneratePRDFlow = usePrevious(generatePRDFlow);

  const generateText = useProjectCreateStore((state) => state.generateText);
  const generateAttachedFiles = useProjectCreateStore(
    (state) => state.generateAttachedFiles
  );

  const executeCommand = useDesignCommandManager(
    (store) => store.executeCommand
  );

  const sendMessageToIFrame = useIframeStore(
    (store) => store.sendMessageToIFrame
  );

  const featurePrd = useFeaturePrdForCanvas();
  const featurePrdRef = useRef(featurePrd);
  useEffect(() => {
    featurePrdRef.current = featurePrd;
  }, [featurePrd]);

  const onDeleteElement = useCallback(() => {
    if (selectedElement.isEmpty()) {
      return;
    }

    if (selectedElement.isStoryboard()) {
      deleteStoryboards(
        multiSelectedElements?.length
          ? multiSelectedElements
          : [selectedElement]
      );
      return;
    }

    if (!selectedElement.isKnownElement()) {
      toast.error(
        "Element cannot be found in the codebase, try to select a different element or refresh the frame."
      );
      return;
    }

    const removeChange = new RemoveJsxChange({
      codebaseIdsToRemove: multiSelectedElements?.length
        ? multiSelectedElements.map((tempoElement) => tempoElement.codebaseId)
        : [selectedElement.codebaseId],
    });

    // addChange(removeChange);
    executeCommand(removeChange);

    // Deselect all after instant update
    removeChange.clearSelectedElementsAfterInstantUpdate();

    // After an undo, reselect the deleted element
    removeChange.setSelectedElementsAfterUndoInstantUpdate(
      selectedElement.getKey(),
      multiSelectedElements?.map((e) => e.getKey())
    );
  }, [selectedElement, multiSelectedElements, deleteStoryboards]);

  const onDeleteRef = useRef(onDeleteElement);
  useEffect(() => {
    onDeleteRef.current = onDeleteElement;
  }, [onDeleteElement]);

  useEffect(() => {
    if (triggerDeleteElement) {
      setTriggerDeleteElement(false);
      onDeleteElement();
    }
  }, [triggerDeleteElement]);

  const resetAiActionStore = useAiActionStore(
    (state) => state.resetAiActionStore
  );
  const resetAiContextStore = useAiContextStore(
    (state) => state.resetAiContextStore
  );
  const resetDesignSystemStore = useDesignSystemStore(
    (state) => state.resetDesignSystemStore
  );
  const resetCanvasStore = useCanvasStore((state) => state.resetCanvasStore);
  const resetDevServerStore = useDevServerStore(
    (state) => state.resetDevServerStore
  );
  const resetCodebaseStore = useCodebaseStore(
    (state) => state.resetCodebaseStore
  );
  const resetLayoutStore = useLayoutStore((state) => state.resetLayoutStore);
  const resetSupabaseConnectStore = useSupabaseConnectStore(
    (state) => state.resetSupabaseConnectStore
  );
  const resetDesignCommandManagerStore = useDesignCommandManager(
    (state) => state.resetDesignCommandManagerStore
  );
  const setSession = useDesignCommandManager((state) => state.setSession);

  /**
   * When the activity stream changes, if the activity doesn't exist in the undo/redo stack
   * add it in
   */
  const activityStreamList = useActivityStore(
    (state) => state.activityStreamList
  );
  const undoStack = useDesignCommandManager((state) => state.undoStack);
  const redoStack = useDesignCommandManager((state) => state.redoStack);
  const serverChangeQueue = useDesignCommandManager(
    (state) => state.serverChangeQueue
  );
  const setRedoStack = useDesignCommandManager((state) => state.setRedoStack);
  const setUndoStack = useDesignCommandManager((state) => state.setUndoStack);

  useEffect(() => {
    if (!activityStreamList?.length) {
      return;
    }

    const resetStacksToActivityStream = () => {
      // Activity stream list is from newest to oldest
      // We pop off the end of the redo or undo stack to get the next activity to undo/redo
      const newRedoStack = activityStreamList
        .filter((activity) => activity.change_undone)
        .map((activity) => {
          const change = new UnknownChange({});
          change.activityId = activity.id;
          return change;
        });
      const newUndoStack = [...activityStreamList]
        .reverse()
        .filter((activity) => !activity.change_undone)
        .map((activity) => {
          const change = new UnknownChange({});
          change.activityId = activity.id;
          return change;
        });

      setRedoStack(newRedoStack);
      setUndoStack(newUndoStack);
    };

    // If the undo & redo stack are empty, add all the activities to it
    if (
      undoStack.length === 0 &&
      redoStack.length === 0 &&
      activityStreamList.length
    ) {
      resetStacksToActivityStream();
      return;
    }

    // Check if the newest activity might be missing from the undo stack
    const newestActivity = activityStreamList[0];

    if (
      !undoStack.find((change) => change.activityId === newestActivity.id) &&
      !redoStack.find((change) => change.activityId === newestActivity.id)
    ) {
      const newActivity = new UnknownChange({});
      newActivity.activityId = newestActivity.id;

      setUndoStack([newActivity, ...undoStack]);
      return;
    }

    // Otherwise, check if the activity stream matches the undo/redo stack assuming there are no queued up server changes
    if (serverChangeQueue?.length) {
      return;
    }

    // 1) Are all activities in the activity stream either in the undo or redo stack?
    // 2) If an activity is undone, it should be in the redo stack
    // 3) If an activity is redone, it should be in the undo stack
    const valid = activityStreamList.every((activity) => {
      if (activity.change_undone) {
        // Activity should be in the redo stack
        return redoStack.find((change) => change.activityId === activity.id);
      } else {
        // Activity should be in the undo stack
        return undoStack.find((change) => change.activityId === activity.id);
      }
    });

    if (!valid) {
      resetStacksToActivityStream();
    }
  }, [activityStreamList]);

  const { recordProjectAccess } = useProjectCacheStore();

  useEffect(() => {
    if ((canvas as Canvas)?.project_id) {
      recordProjectAccess((canvas as Canvas).project_id);
    }
  }, [canvas]);

  /**
   * When the user opens a new editor, reset the editor state
   */
  useEffect(() => {
    if (canvasId) {
      resetLayoutStore();
      dispatch(resetCodebaseState());
      dispatch(resetGithubState());
      resetAiActionStore();
      resetAiContextStore();
      resetDesignSystemStore();
      resetCanvasStore();
      resetCodebaseStore();
      resetSupabaseConnectStore();
      resetDevServerStore();
      resetDesignCommandManagerStore(canvasId);
    }
  }, [canvasId]);

  useEffect(() => {
    if (!session) {
      return;
    }

    setSession(session);
  }, [session]);

  useEffect(() => {
    if (!canvasId || !mainCanvas?.project_id) {
      return;
    }

    track("PROJECT_OPEN");
  }, [canvasId, mainCanvas?.project_id]);

  /**
   * Construct a possible file that can be used to either copy/paste
   * or to drag out of a storyboard for the selected nav tree node
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    dispatch(setExtractedComponentFile(null));
    dispatch(setExtractedComponentFilePath(null));

    let newExtractedElements, error;
    if (multiselectedNavTreeNodes?.length) {
      newExtractedElements = multiselectedNavTreeNodes.map((navTreeNode) => {
        const { extractedElement, error: innerError } = getExtractedElement(
          navTreeNode,
          treeElementLookup
        );
        error = innerError || error;
        return extractedElement;
      });
    } else if (selectedNavTreeNode) {
      const { extractedElement, error: innerError } = getExtractedElement(
        selectedNavTreeNode,
        treeElementLookup
      );
      error = innerError;
      newExtractedElements = [extractedElement];
    }

    if (error) {
      // This same process is attempted again when pasting (from the selected element key)
      // So this error will show up if that fails as well
      return;
    }

    if (newExtractedElements?.length) {
      getUnexpiredToken(session).then((supabaseJWT: string) => {
        getComponentFileFromExtractedElements(
          canvasId,
          supabaseJWT,
          newExtractedElements
        )
          .then(({ newFileContents, dummyFilePath }) => {
            dispatch(setExtractedComponentFile(newFileContents));
            dispatch(setExtractedComponentFilePath(dummyFilePath));
          })
          .catch((error) => {
            logAxiosError("Error extracting component", error);
          });
      });
    }
  }, [selectedNavTreeNode, multiselectedNavTreeNodes, Boolean(session)]);

  /**
   * Update the iframes whenever the tree elements have changed
   */
  useEffect(() => {
    if (
      !canvasId ||
      !treeElementLookup ||
      !scopeLookup ||
      Object.keys(treeElementLookup).length === 0 ||
      Object.keys(prevChanges).length === 0
    ) {
      return;
    }

    const changesToSend = {
      treeElementLookup:
        // @ts-ignore
        prevChanges.treeElementLookup === "ALL"
          ? treeElementLookup
          : // @ts-ignore
            prevChanges.treeElementLookup || {},
      scopeLookup:
        // @ts-ignore
        prevChanges.scopeLookup === "ALL"
          ? scopeLookup
          : // @ts-ignore
            prevChanges.scopeLookup || {},
    };

    if (
      Object.keys(changesToSend.treeElementLookup).length === 0 &&
      Object.keys(changesToSend.scopeLookup).length === 0
    ) {
      return;
    }

    const treeElementToSend = getTreeElementLookupToSend(
      changesToSend.treeElementLookup
    );

    // Update inside the iframes too
    // Send to all canvases
    sendMessageToIFrame({
      type: "executeFunction",
      payload: {
        functionName: "setNewLookups",
        args: [treeElementToSend, changesToSend.scopeLookup],
      },
    });

    clearPrevChanges();
  }, [treeElementLookup, canvasId, prevChanges]);

  /**
   * Delete selected element when backspace is pressed
   */
  useEffect(() => {
    const mousetrapCallback = async (e: any) => {
      if (e.preventDefault) {
        e.preventDefault();
      } else {
        // internet explorer
        e.returnValue = false;
      }

      onDeleteRef.current();
    };

    const backspaceSync = (e) => {
      mousetrapCallback(e);
    };

    Mousetrap.bind("backspace", backspaceSync);

    return () => {
      Mousetrap.unbind("backspace");
    };
  }, []);

  /**
   * Set files in the editor whenever the selectedElement or css selector changes
   */
  useEffect(() => {
    if (!prevSelectedElement || selectedElement.isEqual(prevSelectedElement)) {
      return;
    }

    if (selectedElement.isStoryboard()) {
      const selectedStoryboard = canvas?.storyboards?.find(
        (storyboard) => storyboard.id === selectedElement.storyboardId
      );

      if (selectedStoryboard?.fullFilePath) {
        openFile({
          file: {
            filename: selectedStoryboard?.fullFilePath,
            hardOpened: false,
            context: EditorContext.Code,
          },
          jumpToTab: true,
        });
      }
      return;
    }

    if (selectedTreeElement) {
      openFile({
        file: {
          filename: selectedTreeElement?.filename,
          highlightLines: {
            charPos: selectedTreeElement?.start,
          },
          hardOpened: false,
          context: EditorContext.Code,
        },
        jumpToTab: true,
      });
      return;
    }
  }, [selectedElement]);

  useEffect(() => {
    if (canvasId) {
      closeAllFiles();
    }
  }, [canvasId]);

  const fetchGitAndFileTree = useRef(getGetAndFileTreeFunction());

  useEffect(() => {
    if (!session) {
      return;
    }

    fetchGitAndFileTree.current(session, canvasId, dispatch);
  }, [projectLaunchStatus, iframeChangedKey, canvasId, Boolean(session)]);

  /**
   * Anytime the nav tree changes, check if there is a pending selection codebase id.
   * If there is, select it.
   */
  useEffect(() => {
    if (!pendingSelectionCodebaseId) {
      return;
    }

    const pendingSelectionNavNodes =
      codebaseIdToNavNodeLookup[pendingSelectionCodebaseId] || [];

    if (!pendingSelectionNavNodes.length) {
      console.debug("No pending selection nav node");
      return;
    }

    let pendingSelectionNavNode: NavTreeNode = pendingSelectionNavNodes[0];

    if (pendingSelectionNavNodes.length > 1) {
      console.debug(
        "Found multiple pending selection nav nodes, picking the first one"
      );
    }

    dispatch(
      setSelectedElementKey(pendingSelectionNavNode.tempoElement.getKey())
    );
    setPendingSelectionCodebaseId(null);
  }, [codebaseIdToNavNodeLookup]);

  /**
   * Open nav tree to given node when element is selected
   */
  useEffect(() => {
    // This check can be true even if codebase ID is not set
    if (!selectedElement.isEmpty()) {
      let navNodeToOpen =
        elementKeyToNavNodeLookup[selectedElement.getKey()]?.parent;
      while (navNodeToOpen) {
        nodeOpenDict[navNodeToOpen.tempoElement.getKey()] = true;
        navNodeToOpen = navNodeToOpen.parent;
      }
      setNodeOpenDict({ ...nodeOpenDict });
    }
  }, [selectedElement]);

  /**
   * This use effect reacts to the user selecting a new element either by drag & dropping,
   * selecting in the dom tree or drawing, and pushing that to the iframes.
   */
  useEffect(() => {
    // Only trigger these actions if the selected element has changed
    if (prevSelectedElement && selectedElement.isEqual(prevSelectedElement)) {
      return;
    }

    const elementKey = selectedElement.getKey();

    // This check can be true even if codebase ID is not set
    if (!selectedElement.isEmpty()) {
      sendMessageToIFrame({
        type: "executeFunction",
        payload: {
          functionName: "setSelectedElement",
          args: [elementKey],
        },
      });
    } else {
      sendMessageToIFrame({
        type: "executeFunction",
        payload: {
          functionName: "setSelectedElement",
          args: [null],
        },
      });
    }
  }, [selectedElement]);

  /**
   * This use effect reacts to the user multi-selecting a new element either by selecting in the DOM
   * tree or in the outer canvas
   */
  useEffect(() => {
    // Don't send if nothing has changed
    if (prevMultiSelectedElementKeys) {
      const prevSet = new Set(prevMultiSelectedElementKeys || []);
      const newSet = new Set(multiSelectedElementKeys || []);

      const setsEqual =
        prevSet.size === newSet.size &&
        [...prevSet].every((x: string) => newSet.has(x));

      if (setsEqual) {
        return;
      }
    }

    sendMessageToIFrame({
      type: "executeFunction",
      payload: {
        functionName: "setMultiselectedElementKeys",
        args: [multiSelectedElementKeys],
      },
    });
  }, [multiSelectedElementKeys]);

  /**
   * When hovered element changes, send that to the backend
   */
  useEffect(() => {
    // Hovered element hasn't changed
    if (prevHoveredElement && hoveredElement.isEqual(prevHoveredElement)) {
      return;
    }

    if (hoveredElement.isKnownElement()) {
      const expectedCodebaseId =
        elementKeyToNavNodeLookup[hoveredElement.getKey()]?.tempoElement
          .codebaseId;

      // The nav tree is out of sync, this can happen if there is an instant update that updates the nav tree
      // but the element key hasn't changed yet
      // Don't send to any updates to the iframes as it can cause outline flashing
      if (expectedCodebaseId !== hoveredElement.codebaseId) {
        return;
      }
    }

    const elementKey = hoveredElement.getKey();

    // This check can be true even if codebase ID is not set
    if (!hoveredElement.isEmpty()) {
      sendMessageToIFrame({
        type: "executeFunction",
        payload: {
          functionName: "setHoveredElement",
          args: [elementKey],
        },
      });
    } else {
      sendMessageToIFrame({
        type: "executeFunction",
        payload: {
          functionName: "setHoveredElement",
          args: [null],
        },
      });
    }
  }, [hoveredElement]);

  useEffect(() => {
    if (!supabaseClient) {
      return;
    }

    const generateTitles = async () => {
      const url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/ai/vercel-ai/aiGenerateTitleAndProjectName`;

      const supabaseJWT = await getUnexpiredToken(session);

      const body = {
        visual_context:
          generateAttachedFiles && generateAttachedFiles.length > 0
            ? generateAttachedFiles.map((file) => ({
                imageUrl: file.url,
                context: "Image related to the app",
              }))
            : undefined,
        generateText,
      };

      const stream = await generateJsonStream(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      let res = null;

      for await (let chunk of stream) {
        if (chunk.status === -1 && chunk.message === "intermediate") {
          // No straming
        } else {
          res = chunk;
          break;
        }
      }

      // @ts-expect-error TS18047
      if (res.status >= 400) {
        console.error({
          // @ts-expect-error TS18047
          msg: res.message,
          // @ts-expect-error TS18047
          internalErrorStr: res.internalErrorStr,
        });
        // @ts-expect-error TS18047
        throw new Error(res.message);
      }

      // @ts-expect-error TS18047
      const finalResult = res.data.steps[0].text;
      const parsedResult = fixJson(finalResult).fixedObj;

      await supabaseClient
        .from("feature_prd")
        .update({ title: parsedResult.feature_title })
        .eq("canvas_id", canvasId);

      onDbSnapshot("featurePrds", {
        newItems: [
          { ...featurePrdRef.current, title: parsedResult.feature_title },
        ],
      });

      const updatedProject = {
        ...project,
        name: parsedResult.project_name,
      };

      dispatch(
        onProjectsSnapshot({
          newItems: [updatedProject],
          deletedItems: [updatedProject.id],
        })
      );

      await supabaseClient
        .from("projects")
        .update({ name: parsedResult.project_name })
        .eq("id", project.id);
    };

    if (
      project &&
      generateText &&
      prevGeneratePRDFlow &&
      !generatePRDFlow &&
      featurePrd?.prd
    ) {
      try {
        generateTitles();
      } catch (e) {
        console.error(e);
      } finally {
        refreshPlanInfo(session);
      }
    }
  }, [featurePrd, project, generatePRDFlow, supabaseClient]);

  /**
   * When files are changed, load them from the backend
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    loadAllNewFiles(canvasId, session);
  }, [filesToReload, Boolean(session), editorContext, editorState]);

  const setUsersForProject = useCanvasStore(
    (state) => state.setUsersForProject
  );

  /**
   * Fetch users for the project
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    async function fetchUsers() {
      const supabaseJWT = await getUnexpiredToken(session);
      const axiosInstance = axios.create({
        baseURL: import.meta.env.VITE_BACKEND_BASE_URL,
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
      });

      const usersResp = await axiosInstance.get(
        `/projects/${mainCanvas?.project_id}/users`
      );

      setUsersForProject(
        mainCanvas?.project_id,
        usersResp?.data.users.map((user: any) => ({
          name: user.name,
          email: user.email,
          id: user.auth0_user_id,
          picture: user.picture,
          is_selected: false,
        })) || []
      );
    }

    if (mainCanvas?.project_id) {
      fetchUsers();
    }
  }, [mainCanvas?.project_id, Boolean(session)]);

  /**
   * Fetch figma project id if existing
   */
  useEffect(() => {
    if (!figmaContextId || !session || projectLaunchStatus !== "succeeded")
      return;

    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete("figmaContextId");
      return newParams;
    });
    // @ts-expect-error TS2345
    setNewLayout(null, LayoutMode.Design);

    track("FIGMA_IMPORT", {
      figmaContextId,
    });

    getUnexpiredToken(session).then((supabaseJWT: string) => {
      axios
        .get(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/figma/context/${figmaContextId}`,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((response) => {
          if (response.status === 404) {
            toast.error(
              "Figma context not found, may have already been fetched and deleted."
            );
            console.error(
              "Figma context not found, may have already been fetched and deleted."
            );
            return;
          }
          if (response.status !== 200) {
            toast.error(`Error fetching Figma data: ${response}`);
            console.error(`Error fetching Figma data: ${response}`);
            return;
          }
          const { initial_code, image_url, figma_context, frame_images } =
            response.data;

          let figmaImages = "";
          let figmaIcons = "";
          try {
            const frameImagesJson = JSON.parse(frame_images);

            for (const image of frameImagesJson.images) {
              figmaImages += `\n<image_${image.nodeId}>\n${image.url}\n</image_${image.nodeId}>\n`;
            }

            for (const icon of frameImagesJson.icons) {
              figmaIcons += `\n<icon_${icon.nodeId}>\n${icon.svg}\n</icon_${icon.nodeId}>\n`;
            }
          } catch (e) {
            console.error(e);
            toast.error("Error parsing Figma data, please try again.");
            return;
          }

          const figmaMessage = constructFigmaMessage(
            initial_code,
            figma_context,
            figmaImages,
            figmaIcons
          );

          setSendChatMessage({
            sendChatMessage: `\n<figma_input>\n${figmaMessage}\n</figma_input>\n`,
            sendChatMessageFiles: [
              {
                url: image_url,
                name: "figma_image",
                type: "image",
                mimeType: "image/png",
              },
            ],
            resetToggles: "all",
          });
        })
        .catch((error) => {
          toast.error(
            `Error fetching Figma data: ${getAxiosErorrMessage(error)}`
          );
          console.error(
            "Error fetching Figma data:",
            getAxiosErorrMessage(error)
          );
        });
    });
  }, [figmaContextId, Boolean(session), projectLaunchStatus]);

  const canvasToMachineMapping = useCanvasStore(
    (state) => state.canvasToMachineMapping
  );
  const machineForCanvas = canvasToMachineMapping[canvasId];

  useEffect(() => {
    if (!canvasId) {
      return;
    }

    if (canvasId && !machineForCanvas) {
      // Fetch the machine and set it in the store
      getMachineForCanvas(canvasId, supabaseClient);
    }
  }, [machineForCanvas]);

  return <>{renderConfirmDeleteDialog()}</>;
});
